import { useSyncExternalStore } from "react";
import { jsonHeaders } from "./api";

/**
 * Moduler som kan skjules for alle under Innstillinger → Moduler (Erik 01.11.2026).
 * Lagres i API-et (GET/PUT /api/innstillinger/moduler). Før det API-et er rullet ut
 * (404), lagres valget bare i denne nettleseren, og siden sier fra om det.
 */
export type ModulId = "prosjekter" | "kalkyle" | "salg";

export const MODULER: { id: ModulId; navn: string; beskrivelse: string; ruter: string[] }[] = [
  { id: "prosjekter", navn: "Prosjekter", beskrivelse: "Prosjektoversikten i menyen", ruter: ["/prosjekter"] },
  { id: "kalkyle", navn: "Kalkyle", beskrivelse: "Kalkylemotoren: oversikt, ny kalkyle og rapport", ruter: ["/kalkyle"] },
  { id: "salg", navn: "Salg", beskrivelse: "Salgsoversikten og nyeste salg", ruter: ["/salg", "/salg/nyeste"] },
];

export type ModulTilstand = {
  skjulte: ModulId[];
  /** "api" = gjelder alle; "nettleser" = API-et mangler, gjelder bare her */
  kilde: "api" | "nettleser";
  endretAv: string | null;
  endret: string | null;
};

const LOKAL_KEY = "gb-skjulte-moduler";
const URL = "/api/innstillinger/moduler";

function lesLokalt(): ModulId[] {
  try {
    const v = JSON.parse(localStorage.getItem(LOKAL_KEY) ?? "[]") as unknown;
    return Array.isArray(v) ? v.filter((m): m is ModulId => MODULER.some((x) => x.id === m)) : [];
  } catch {
    return [];
  }
}

function skrivLokalt(skjulte: ModulId[]): void {
  try {
    localStorage.setItem(LOKAL_KEY, JSON.stringify(skjulte));
  } catch {
    /* privat modus o.l. */
  }
}

// Siste kjente valg ligger i nettleseren, så menyen ikke blinker før API-svaret.
let tilstand: ModulTilstand = { skjulte: lesLokalt(), kilde: "nettleser", endretAv: null, endret: null };
const lyttere = new Set<() => void>();
let hentet = false;

function sett(ny: ModulTilstand): void {
  tilstand = ny;
  skrivLokalt(ny.skjulte);
  lyttere.forEach((l) => l());
}

async function hent(): Promise<void> {
  try {
    const r = await fetch(URL);
    if (!r.ok) return; // 404 før deploy: behold valget fra nettleseren
    const d = (await r.json()) as Omit<ModulTilstand, "kilde">;
    sett({ ...d, skjulte: d.skjulte.filter((m) => MODULER.some((x) => x.id === m)), kilde: "api" });
  } catch {
    /* nettverksfeil: behold valget fra nettleseren */
  }
}

function abonner(l: () => void): () => void {
  lyttere.add(l);
  if (!hentet) {
    hentet = true;
    void hent();
  }
  return () => lyttere.delete(l);
}

export function useModuler(): ModulTilstand {
  return useSyncExternalStore(abonner, () => tilstand);
}

/** Lagrer hvilke moduler som er skjult. Faller tilbake til nettleseren hvis API-et mangler. */
export async function lagreSkjulteModuler(skjulte: ModulId[]): Promise<void> {
  const r = await fetch(URL, { method: "PUT", headers: jsonHeaders(), body: JSON.stringify({ skjulte }) });
  if (r.status === 404 || r.status === 405) {
    sett({ skjulte, kilde: "nettleser", endretAv: null, endret: null });
    return;
  }
  if (!r.ok) throw new Error(`Kunne ikke lagre (${r.status} ${r.statusText})`);
  const d = (await r.json()) as Omit<ModulTilstand, "kilde">;
  sett({ ...d, kilde: "api" });
}

/** Er stien en side i en skjult modul? */
export function erSkjultRute(pathname: string, skjulte: ModulId[]): boolean {
  return MODULER.some((m) => skjulte.includes(m.id) && m.ruter.includes(pathname));
}
